<script setup lang="ts">
import { DateTime } from 'luxon'
import type { ScheduleEvent, Team } from '~/types'
import { IN_GAME_STATUSES, PRE_GAME_STATUSES } from '~/enums/schedule-event'

defineOptions({
  name: 'ScheduleEventsNextGame',
})

interface Props {
  team: Team.Model
  scheduleId: string
}

const props = defineProps<Props>()
const scheduleId = computed<number>(() => Number(props.scheduleId))
const refreshInterval = ref<NodeJS.Timeout>()
const { id: teamId, nextEventBanners: nextEventBannersTeam } = useTeam(props.team)

const scheduleNextEventParams = computed<ScheduleEvent.SearchRequest>(() => {
  const starDate = DateTime.now().minus({ hour: 6 }).startOf('hour')

  return {
    include: [
      'awayTeam.logo',
      'homeTeam.logo',
      'nextEventBanners.image',
      'scheduleEventBroadcasts.logo',
      'scheduleEventLinks',
      'scheduleEventScoring',
      'scheduleEventSponsors.logo',
      'venue',
    ],
    per_page: 1,
    filter: {
      'scheduleWeek.schedule_id': scheduleId.value ?? undefined,
      'team_id': teamId.value ?? undefined,
      'datetime': `>${starDate.toISO()}`,
      'status': [
        ...PRE_GAME_STATUSES,
        ...IN_GAME_STATUSES,
      ],
    },
    sort: [
      'datetime',
    ],
  }
})

const { fetchData: loadNextEvents } = useLoadScheduleEvents(scheduleNextEventParams)
const { data: items, refresh } = await useAsyncData('next-schedule-event', () => loadNextEvents(), { watch: [scheduleNextEventParams] })

const nextEvent = computed<Maybe<ScheduleEvent.Model>>(() => items.value?.data?.[0] || null)

const { isPreGame, isLive } = useScheduleEvent(nextEvent)

const datetime = computed<Maybe<string>>(() => nextEvent.value?.datetime || null)

const time = computed<Maybe<Date>>(() => {
  if (!datetime.value) {
    return null
  }
  return new Date(datetime.value)
})

onMounted(() => {
  refreshInterval.value = setInterval(() => {
    refresh()
  }, 5 * 60 * 1000)
})

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
  }
})
</script>

<template>
  <section
    v-if="nextEvent"
    class="next-game"
  >
    <Wrapper>
      <div class="next-game__inner">
        <div class="next-game__event">
          <div class="next-game__top">
            <div class="next-game__heading">
              <span class="next-game__label">Next Game</span>
              <ScheduleEventsNextGameTime
                :team
                :schedule-event="nextEvent"
                class="next-game__date"
              />
            </div>
            <ScheduleEventsNextGameCountdown
              v-if="time && isPreGame"
              :time="time"
              class="next-game__countdown"
            />
            <div v-else-if="isLive">
              <ScheduleEventsLiveBadge
                :schedule-event="nextEvent"
                class="next-game__live-badge"
              />
            </div>
          </div>
          <ScheduleEventsNextGameItem
            :schedule-event="nextEvent"
            :current-team="team"
            class="next-game__content"
          />
        </div>
        <ScheduleEventsNextGameBanners
          :schedule-event="nextEvent"
          :next-event-banners-team
          class="next-game__thumb"
        />
      </div>
    </Wrapper>
  </section>
</template>

<style scoped lang="scss">
.next-game {
  @apply bg-primary/10 mb-[40px] py-[40px] pb-[60px];
  @apply lg:mb-[55px] lg:pt-[55px];
  @apply xl:pb-[80px];

  &__inner {
    @apply flex flex-wrap relative z-[1] bg-[var(--team-color)] rounded-[10px];
    @apply after:content-[''] after:absolute after:-bottom-[25px] after:left-1/2 after:-translate-x-1/2 after:rounded-[70%] after:h-[70px] after:w-[80%] after:blur-[25px] after:block after:bg-primary after:opacity-50 after:-z-[1];
  }

  &__event,
  &__thumb {
    @apply w-full;
    @apply md:w-1/2;
  }

  &__event {
    @apply bg-white rounded-[10px] py-[30px] px-[20px] flex flex-col items-start justify-between;
    @apply md:rounded-l-[10px] md:rounded-r-none md:py-[40px] md:px-[30px];
    @apply xl:py-[50px] xl:px-[60px];
  }

  &__top {
    @apply flex flex-col justify-between gap-[20px] w-full;
    @apply xl:flex-row xl:gap-[50px];
  }

  &__heading {
    @apply flex flex-col text-[18px] font-medium leading-[1];
  }

  &__label {
    @apply text-primary;
  }

  &__content {
    @apply w-full flex flex-col gap-[15px] items-start mt-[40px];
    @apply xl:gap-[60px] xl:mt-[60px];
  }

  &__subtitle {
    @apply text-[16px] text-primary;
  }

  &__divider {
    @apply ml-[65px] text-[16px] text-primary/60 uppercase leading-[1];
    @apply xl:ml-[80px];
  }

  &__teams {
    @apply flex flex-col gap-[10px] w-full;
    @apply lg:gap-[20px];
    @apply xl:gap-[15px];
  }

  &__team {
    @apply flex items-center justify-between;

    &-logo {
      @apply mr-[15px] w-[50px] h-[50px] rounded-full border border-primary/20 flex items-center justify-center;
      @apply xl:mr-[30px];
    }

    &-image {
      @apply w-[80%] h-[80%] object-contain;
    }

    &-name {
      @apply mr-auto text-[22px] text-primary block leading-[1] font-medium;
      @apply md:text-[26px] md:font-normal;
      @apply lg:text-[40px];
      @apply xl:text-[60px];
    }
  }

  &__location {
    @apply text-[16px] text-primary/60;
  }

  &__links {
    @apply flex justify-center w-full gap-[10px] flex-wrap mt-[35px];
    @apply md:justify-start;
  }

  &__thumb {
    @apply rounded-r-[10px] overflow-hidden relative hidden;
    @apply md:min-h-[480px] md:block;
    @apply xl:min-h-[550px];
  }

  &__image {
    @apply absolute left-0 top-0 w-full h-full object-cover;
  }

  &__live-badge {
    @apply ml-auto mb-[15px];
  }
}
</style>
